import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { VISIBILITY } from '../../Api/VisibilityApi';
import styles from './VisibilitySelector.module.css';

/**
 * Visibility picker shared by the four flows : document upload, template
 * creation, send for sign and sign myself.
 *
 *   Public                       -> everybody in the organization
 *   Private / Only me            -> the creator only
 *   Private / Selected users     -> the creator plus the picked users
 *
 * The organization users come from the init payload the app already loads, so
 * this component does not add a request of its own. The ids it sends are only
 * a request : the backend re-checks that every one of them belongs to the same
 * organization before storing anything.
 */
const VisibilitySelector = ({
  visibility = VISIBILITY.PUBLIC,
  selectedUserIds = [],
  onChange,
  disabled = false,
  ownerId,
}) => {
  const show_integeration = useSelector((state) => state.welcomeImgDetail.value);
  const upload_details = useSelector((state) => state.document.value);

  const currentUserId = ownerId ?? show_integeration?.user_information?.Id;

  const [search, setSearch] = React.useState('');

  const orgUsers = useMemo(() => {
    const users = show_integeration?.organizations_users || [];
    // the owner is always granted, so he is not part of the picker
    return users
      .filter((user) => user?.UserId && String(user.UserId) !== String(currentUserId))
      .map((user) => ({
        id: user.UserId,
        name: [user.FirstName, user.LastName].filter(Boolean).join(' ') || user.Email,
        email: user.Email,
      }));
  }, [show_integeration?.organizations_users, currentUserId]);

  const visibleUsers = useMemo(() => {
    if (!search?.trim()) return orgUsers;
    const term = search.trim().toLowerCase();
    return orgUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term)
    );
  }, [orgUsers, search]);

  const isPrivate = visibility !== VISIBILITY.PUBLIC;

  const emit = (nextVisibility, nextUserIds) => {
    onChange({ visibility: nextVisibility, selectedUserIds: nextUserIds });
  };

  const handleVisibility = (value) => {
    if (value === 'public') {
      emit(VISIBILITY.PUBLIC, []);
    } else {
      emit(VISIBILITY.PRIVATE_ONLY_ME, []);
    }
  };

  const handlePrivateMode = (value) => {
    if (value === 'only_me') {
      emit(VISIBILITY.PRIVATE_ONLY_ME, []);
    } else {
      emit(VISIBILITY.PRIVATE_SELECTED_USERS, selectedUserIds);
    }
  };

  const toggleUser = (userId) => {
    const exists = selectedUserIds.some((id) => String(id) === String(userId));
    const next = exists
      ? selectedUserIds.filter((id) => String(id) !== String(userId))
      : [...selectedUserIds, userId];
    emit(VISIBILITY.PRIVATE_SELECTED_USERS, next);
  };

  return (
    <Box className={styles.wrapper}>
      <Typography className={styles.section_title}>Visibility</Typography>

      <RadioGroup
        row
        value={isPrivate ? 'private' : 'public'}
        onChange={(e) => handleVisibility(e.target.value)}
      >
        <FormControlLabel
          value='public'
          disabled={disabled}
          control={<Radio size='small' className={styles.radio} />}
          label={<span className={styles.option_label}>Public</span>}
        />
        <FormControlLabel
          value='private'
          disabled={disabled}
          control={<Radio size='small' className={styles.radio} />}
          label={<span className={styles.option_label}>Private</span>}
        />
      </RadioGroup>

      <Typography className={styles.hint}>
        {isPrivate
          ? 'Only you and the people you choose can access this.'
          : 'Everyone in your organization can access this.'}
      </Typography>

      {isPrivate && (
        <Box className={styles.private_block}>
          <Typography className={styles.section_title}>Private access</Typography>

          <RadioGroup
            row
            value={
              visibility === VISIBILITY.PRIVATE_SELECTED_USERS ? 'selected_users' : 'only_me'
            }
            onChange={(e) => handlePrivateMode(e.target.value)}
          >
            <FormControlLabel
              value='only_me'
              disabled={disabled}
              control={<Radio size='small' className={styles.radio} />}
              label={<span className={styles.option_label}>Only me</span>}
            />
            <FormControlLabel
              value='selected_users'
              disabled={disabled}
              control={<Radio size='small' className={styles.radio} />}
              label={<span className={styles.option_label}>Selected users</span>}
            />
          </RadioGroup>

          {visibility === VISIBILITY.PRIVATE_SELECTED_USERS && (
            <Box className={styles.user_block}>
              {orgUsers.length === 0 ? (
                <Typography className={styles.empty}>
                  There is no other user in {upload_details?.OrgName || 'this organization'} yet.
                </Typography>
              ) : (
                <>
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='Search users'
                    value={search}
                    disabled={disabled}
                    onChange={(e) => setSearch(e.target.value)}
                    className={styles.user_search}
                    InputProps={{ disableUnderline: true }}
                    variant='standard'
                  />
                  <Box className={styles.user_list}>
                    {visibleUsers.map((user) => (
                      <FormControlLabel
                        key={user.id}
                        className={styles.user_row}
                        disabled={disabled}
                        control={
                          <Checkbox
                            size='small'
                            className={styles.radio}
                            checked={selectedUserIds.some(
                              (id) => String(id) === String(user.id)
                            )}
                            onChange={() => toggleUser(user.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography className={styles.user_name}>{user.name}</Typography>
                            <Typography className={styles.user_email}>{user.email}</Typography>
                          </Box>
                        }
                      />
                    ))}
                    {visibleUsers.length === 0 && (
                      <Typography className={styles.empty}>No user matches your search.</Typography>
                    )}
                  </Box>
                  {selectedUserIds.length === 0 && (
                    <Typography className={styles.warning}>
                      Select at least one user, or switch to "Only me".
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VisibilitySelector;
